import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useDataProvider, useStore, useStoreContext } from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import {
  CONFIGURATION_STORE_KEY,
  configKeysEditedSince,
  type ConfigurationContextValue,
} from "./ConfigurationContext";

export const useConfigurationLoader = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const store = useStoreContext();
  // Writes the store directly, not through useConfigurationUpdater, so what
  // the loader brings in is never mistaken for a user's edit.
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );

  const { data } = useQuery({
    // New key (the query cache is persisted in localStorage): an entry
    // cached under the old key has the old, unwrapped shape.
    queryKey: ["configuration", "with-fetch-start"],
    queryFn: async () => {
      const startedAt = Date.now();
      return { startedAt, config: await dataProvider.getConfiguration() };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  useEffect(() => {
    if (!data || Object.keys(data.config ?? {}).length === 0) return;
    // T2: anything the user changed while this fetch was in flight is newer
    // than what came back - keep it, and take every other key from the
    // database. Replacing the whole object used to revert an edit made
    // during startup, and the next save then wrote the old value.
    const edited = configKeysEditedSince(data.startedAt);
    if (edited.length === 0) {
      setConfig(data.config);
      return;
    }
    const local = (store.getItem(CONFIGURATION_STORE_KEY) ?? {}) as Record<
      string,
      unknown
    >;
    const merged = { ...data.config } as unknown as Record<string, unknown>;
    for (const key of edited) merged[key] = local[key];
    setConfig(merged as unknown as ConfigurationContextValue);
  }, [data, setConfig, store]);
};
